import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Modal,
  Form,
  Radio,
  Select,
  InputNumber,
  Row,
  Col,
  Switch,
  Tabs,
  Tag,
  message,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";
import { API } from "../../../../helpers/API/amplifyApi";
import RoleRulesTable from "./RoleRulesTable";
import "./rotacioni_turneve.scss";

const { Option } = Select;
const UNTEAMED = "_unteamed";

const blankFor = (department, shifts) => ({
  departmentId: department?.departmentId || null,
  departmentName: department?.departmentName || null,
  shiftIds: {
    morningShiftId: shifts?.[0]?.shiftId || null,
    afternoonShiftId: shifts?.[1]?.shiftId || null,
  },
  roleRules: [],
  excludedEmployeeIds: [],
  targetAfternoonWeeksPerEmployeePerMonth: 1,
  fairnessMode: "quota",
  autoPublishDayOfMonth: 25,
  weekStartDay: 1,
  status: "active",
});

function PolicyModal({
  open,
  onClose,
  initial,
  departments = [],
  shifts = [],
  employees = [],
  onSaved,
}) {
  const { isDarkMode } = useSelector((state) => state.usersList);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Departments not yet covered by another policy (excluding the current one).
  const isEditing = !!initial?.policyId;

  const selectedDepartmentId = Form.useWatch("departmentId", form);
  const fairnessMode = Form.useWatch("fairnessMode", form);
  const selectedDepartment = useMemo(
    () => departments.find((d) => d.departmentId === selectedDepartmentId),
    [departments, selectedDepartmentId]
  );

  const eligibleEmployees = useMemo(
    () =>
      (employees || []).filter(
        (e) => e?.employeeDepartmentId === selectedDepartmentId
      ),
    [employees, selectedDepartmentId]
  );

  // Auto-discover the distinct teams (locations) present in this
  // department. Each team gets its own quotas tab so admins can set
  // "3D: 1 sr + 1 jr in Tirana, 0 sr + 1 jr in Prishtina" etc.
  const teamsInDept = useMemo(() => {
    const counts = new Map();
    for (const e of eligibleEmployees) {
      if ((e?.employeeStatus || "").toLowerCase() !== "aktiv") continue;
      const t = e?.employeeTeam || UNTEAMED;
      counts.set(t, (counts.get(t) || 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [eligibleEmployees]);

  const [activeTeam, setActiveTeam] = useState(null);
  useEffect(() => {
    if (!teamsInDept.length) {
      setActiveTeam(null);
      return;
    }
    setActiveTeam((prev) =>
      prev && teamsInDept.some((t) => t.name === prev) ? prev : teamsInDept[0].name
    );
  }, [teamsInDept]);

  useEffect(() => {
    if (!open) return;
    const seed = initial
      ? {
          ...initial,
          status: initial.status === "paused" ? "paused" : "active",
        }
      : blankFor(departments[0], shifts);
    form.setFieldsValue({
      departmentId: seed.departmentId,
      morningShiftId: seed.shiftIds?.morningShiftId,
      afternoonShiftId: seed.shiftIds?.afternoonShiftId,
      roleRules: seed.roleRules || [],
      excludedEmployeeIds: seed.excludedEmployeeIds || [],
      targetAfternoonWeeksPerEmployeePerMonth:
        seed.targetAfternoonWeeksPerEmployeePerMonth ?? 1,
      fairnessMode:
        seed.fairnessMode === "everyone_once" ? "everyone_once" : "quota",
      autoPublishDayOfMonth: seed.autoPublishDayOfMonth ?? 25,
      status: seed.status,
    });
  }, [open, initial, departments, shifts, form]);

  const onSave = async () => {
    try {
      const v = await form.validateFields();
      const newMode =
        v.fairnessMode === "everyone_once" ? "everyone_once" : "quota";
      const prevMode =
        initial?.fairnessMode === "everyone_once" ? "everyone_once" : "quota";

      // Switching from quota → everyone_once is destructive in spirit:
      // it makes any per-week minSeniors/minJuniors numbers irrelevant.
      // Many users have hit this by accident; require an explicit confirm.
      if (isEditing && prevMode === "quota" && newMode === "everyone_once") {
        const ok = await new Promise((resolve) => {
          Modal.confirm({
            title: "A je i sigurt që do kalosh në modalitetin 'Çdo punonjës 1 javë'?",
            icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
            content: (
              <div>
                Në këtë modalitet, secili punonjës i kualifikuar merr{" "}
                <b>saktësisht 1 javë pasdite në muaj</b>. Kuotat e
                ruajtura (<b>min. seniorë / juniorë në javë</b>) për të
                gjitha rolet dhe ekipet <b>NUK do të aplikohen</b> derisa
                të kthehesh në modalitetin "Sipas kuotave".
                <br />
                <br />
                Të vazhdoj?
              </div>
            ),
            okText: "Po, kalo",
            okButtonProps: { danger: true },
            cancelText: "Anulo",
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) return;
      }

      setSaving(true);

      const dept = departments.find((d) => d.departmentId === v.departmentId);
      const body = {
        departmentId: v.departmentId,
        departmentName: dept?.departmentName || null,
        shiftIds: {
          morningShiftId: v.morningShiftId,
          afternoonShiftId: v.afternoonShiftId,
        },
        roleRules: v.roleRules || [],
        excludedEmployeeIds: v.excludedEmployeeIds || [],
        targetAfternoonWeeksPerEmployeePerMonth:
          Number(v.targetAfternoonWeeksPerEmployeePerMonth || 1),
        fairnessMode: newMode,
        autoPublishDayOfMonth: Number(v.autoPublishDayOfMonth || 25),
        status: v.status,
      };

      if (isEditing) {
        await API.put(
          "shiftRotationPolicies",
          `/shiftRotationPolicies/${initial.policyId}`,
          { body }
        );
        message.success({ key: "policy", content: "Politika u përditësua." });
      } else {
        await API.post("shiftRotationPolicies", "/shiftRotationPolicies", {
          body,
        });
        message.success({ key: "policy", content: "Politika u krijua." });
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      if (e?.errorFields) return;
      console.error(e);
      message.error({
        key: "policy",
        content: "Diçka shkoi keq. Provo përsëri.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEditing ? "Përditëso politikën" : "Krijo politikë rotacioni"}
      open={open}
      onCancel={onClose}
      centered
      destroyOnHidden
      width={960}
      className={`rotacioni-modal ${isDarkMode ? "dark darkHeader" : "lightHeader"}`}
      wrapClassName="rotacioni-modal-wrap"
      maskClosable={false}
      footer={[
        <MondayButton key="x" className="mondayButtonRed" onClick={onClose}>
          Mbyll
        </MondayButton>,
        <MondayButton
          key="save"
          className="mondayButtonGreen"
          spin={saving}
          onClick={onSave}
        >
          Ruaj Ndryshimet
        </MondayButton>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Row gutter={16} className="rotacioni-modal__row">
          <Col span={12}>
            <Form.Item
              label="Departamenti"
              name="departmentId"
              rules={[{ required: true, message: "Zgjidh një departament" }]}
            >
              <Select
                placeholder="Zgjidh departamentin"
                disabled={isEditing}
                showSearch
                optionFilterProp="children"
                classNames={{ popup: { root: isDarkMode ? "darkDropdown" : "" } }}
              >
                {departments.map((d) => (
                  <Option key={d.departmentId} value={d.departmentId}>
                    {d.departmentName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Statusi"
              name="status"
              initialValue="active"
              valuePropName="checked"
              getValueFromEvent={(checked) => (checked ? "active" : "paused")}
              getValueProps={(v) => ({ checked: v === "active" })}
              className="rotacioni-modal__status"
            >
              <Switch checkedChildren="Aktiv" unCheckedChildren="Pauzuar" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Auto-publikim (dita e muajit)"
              name="autoPublishDayOfMonth"
            >
              <InputNumber min={1} max={28} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} className="rotacioni-modal__row">
          <Col span={24}>
            <Form.Item
              label="Mënyra e shpërndarjes"
              name="fairnessMode"
              tooltip={
                "Si shpërndahet pasditja. Quota = sipas minimumeve për (rol, ekip) javore. " +
                "Çdo punonjës nga 1 javë = round-robin që garanton që çdo punonjës merr saktësisht 1 javë pasdite (kur ka kapacitet)."
              }
            >
              <Radio.Group buttonStyle="solid">
                <Radio.Button value="quota">
                  Sipas kuotave (zbaton min. SR + jr)
                </Radio.Button>
                <Radio.Button value="everyone_once">
                  Çdo punonjës 1 javë (kuotat hiqen)
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
            {fairnessMode === "everyone_once" ? (
              <Alert
                style={{ marginBottom: 12 }}
                type="info"
                showIcon
                message="Modaliteti 'Çdo punonjës nga 1 javë'"
                description={
                  <span>
                    Çdo punonjës i kualifikuar merr <b>saktësisht 1 javë</b>{" "}
                    pasdite në muaj — pjesa tjetër në mëngjes. Kuotat për{" "}
                    <b>min. seniorë/juniorë në javë</b> dhe{" "}
                    <b>vitet e eksperiencës</b> të rolit{" "}
                    <b>nuk zbatohen</b> në këtë modalitet; mbulimi i rolit
                    për javë është pasojë e shpërndarjes. Ekipet (Tiranë/
                    Prishtinë) rrotullohen pavarësisht njëra-tjetrës.
                  </span>
                }
              />
            ) : null}
          </Col>
        </Row>

        <Row gutter={16} className="rotacioni-modal__row">
          <Col span={12}>
            <Form.Item
              label="Turni mëngjes"
              name="morningShiftId"
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Zgjidh turnin e mëngjesit"
                classNames={{ popup: { root: isDarkMode ? "darkDropdown" : "" } }}
              >
                {shifts.map((s) => (
                  <Option key={s.shiftId} value={s.shiftId}>
                    {s.shiftName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Turni pasdite"
              name="afternoonShiftId"
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Zgjidh turnin e pasdites"
                classNames={{ popup: { root: isDarkMode ? "darkDropdown" : "" } }}
              >
                {shifts.map((s) => (
                  <Option key={s.shiftId} value={s.shiftId}>
                    {s.shiftName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="rotacioni-modal__row">
          <div style={{ marginBottom: 6, fontWeight: 600 }}>
            Rregullat për rolet e departamentit
            {fairnessMode === "everyone_once" ? (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                Kuotat e rolit nuk zbatohen në këtë modalitet
              </Tag>
            ) : null}
          </div>

          {teamsInDept.length > 1 ? (
            <Tabs
              activeKey={activeTeam || teamsInDept[0]?.name}
              onChange={setActiveTeam}
              size="small"
              items={teamsInDept.map((t) => ({
                key: t.name,
                label: (
                  <span>
                    {t.name === UNTEAMED ? "Pa ekip" : t.name}{" "}
                    <Tag style={{ marginLeft: 4 }}>{t.count}</Tag>
                  </span>
                ),
              }))}
              style={{ marginBottom: 8 }}
            />
          ) : null}

          <Form.Item name="roleRules" noStyle>
            <RoleRulesTableField
              departmentRoles={selectedDepartment?.departmentRoles || []}
              team={activeTeam || teamsInDept[0]?.name || UNTEAMED}
              isDarkMode={isDarkMode}
            />
          </Form.Item>
          <div className="rotacioni-modal__hint">
            <b>Mënyra</b> dhe <b>vitet e eksperiencës</b> janë të përbashkëta për
            të gjithë ekipet. <b>Min. seniorë / juniorë në javë</b> caktohen
            veçanërisht për ekipin <i>{activeTeam === UNTEAMED ? "pa ekip" : activeTeam || "—"}</i>.
            Algoritmi prodhon çdo javë: (Tiranë: x sr + y jr) + (Prishtinë: x sr + y jr).
          </div>
        </div>

        <Row gutter={16} className="rotacioni-modal__row">
          <Col span={16}>
            <Form.Item
              label="Punonjës të përjashtuar"
              name="excludedEmployeeIds"
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="Zgjidh punonjësit që nuk hyjnë në rotacion"
                showSearch
                optionFilterProp="children"
                classNames={{ popup: { root: isDarkMode ? "darkDropdown" : "" } }}
              >
                {eligibleEmployees.map((e) => (
                  <Option key={e.employeeId} value={e.employeeId}>
                    {e.employeeFirstName} {e.employeeLastName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Javë pasdite / muaj (objektiv)"
              name="targetAfternoonWeeksPerEmployeePerMonth"
            >
              <InputNumber min={0} max={4} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

// Small adapter so RoleRulesTable can be used as a Form.Item field.
const RoleRulesTableField = ({
  value,
  onChange,
  departmentRoles,
  team,
  isDarkMode,
}) => (
  <RoleRulesTable
    departmentRoles={departmentRoles}
    value={value || []}
    onChange={onChange}
    team={team}
    isDarkMode={isDarkMode}
  />
);

export default PolicyModal;
